/* ============================================================
   IDEAS CAFE — <ideas-cart-drawer>

   The side cart. Opens on `ideas:item:added`, which every add
   control on the site already dispatches, and re-renders itself
   from the server after any cart change so money formatting stays
   server-side.

   Also intercepts clicks on the header cart link, so the drawer
   opens instead of navigating away mid-order.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('ideas-cart-drawer')) return;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  class IdeasCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.ideas-drawer__panel');
      this.scrim = this.querySelector('.ideas-drawer__scrim');
      this.body = this.querySelector('[data-drawer-body]');
      this.sectionId = this.dataset.sectionId;
      this.autoOpen = this.dataset.autoOpen === 'true';

      this.recsOn = this.dataset.recOff !== 'true';
      this.recUrl = this.dataset.recUrl || routeRoot() + 'recommendations/products';
      this.recLimit = parseInt(this.dataset.recLimit, 10) || 6;
      this.recSeedFallback = this.dataset.recSeed || '';

      this._onAdded = this._onAdded.bind(this);
      this._onCart = this._onCart.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onDocClick = this._onDocClick.bind(this);

      this.addEventListener('click', this._onClick);
      document.addEventListener('ideas:item:added', this._onAdded);
      document.addEventListener('ideas:cart:updated', this._onCart);

      this._bindHeaderCart();
      this._bindQuantity();
      this._bindRecNav();
      this._renderRecs();
    }

    disconnectedCallback() {
      document.removeEventListener('ideas:item:added', this._onAdded);
      document.removeEventListener('ideas:cart:updated', this._onCart);
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('click', this._onDocClick);
      if (this._armTimer) clearTimeout(this._armTimer);
      document.body.classList.remove('ideas-cart-open');
    }

    /* ── Triggers ────────────────────────────────────────── */

    // The add controls dispatch on themselves and it bubbles; the
    // product form dispatches on document. Both land here.
    _onAdded() {
      if (!this.autoOpen) return;
      // Render as well as open. The add request has already resolved by
      // the time this fires, so the server render is correct — waiting
      // for IdeasCart.refresh() to come back would show the previous
      // basket (or the empty state, on a first add) mid-animation.
      this.open();
      this._render();
    }

    _onCart() {
      // Re-render whenever anything changes the cart, so the drawer is
      // never showing a stale basket when it opens.
      this._render();
    }

    _bindHeaderCart() {
      var self = this;
      document.querySelectorAll('[data-cart-link]').forEach(function (link) {
        link.addEventListener('click', function (event) {
          // Modified clicks and middle-clicks should still reach the
          // cart page — only a plain click opens the drawer.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          self.open();
        });
      });
    }

    _onClick(event) {
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      // Clicks outside the panel are handled by _onDocClick, which
      // catches them wherever they land rather than only on the scrim.
    }

    /* ── Quantity, delegated so it survives re-render ────── */

    _bindQuantity() {
      var self = this;
      this.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-qty-change]');
        if (!btn) return;
        event.preventDefault();
        self._change(btn.dataset.line, btn.dataset.quantity);
      });
    }

    _change(line, quantity) {
      var self = this;
      this.setAttribute('aria-busy', 'true');

      fetch(routeRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity: parseInt(quantity, 10) })
      })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) throw new Error(body.description || 'Cart update failed');
            return body;
          });
        })
        .then(function (cart) {
          // Broadcasting triggers _render, which owns the busy flag from
          // here — clearing it now would flash the drawer back to
          // interactive while the new markup is still being fetched.
          if (window.IdeasCart) window.IdeasCart.broadcast(cart);
          else self.removeAttribute('aria-busy');
        })
        .catch(function (err) {
          console.warn('[ideas] drawer quantity change failed', err);
          // Nothing will render, so nothing else will clear it.
          self.removeAttribute('aria-busy');
        });
    }

    /* ── Render ──────────────────────────────────────────── */

    _render() {
      if (!this.sectionId || !this.body) return;

      if (this._pending) this._pending.abort();
      var controller = new AbortController();
      this._pending = controller;
      this.setAttribute('aria-busy', 'true');

      var self = this;
      return fetch(routeRoot() + '?section_id=' + this.sectionId, {
        signal: controller.signal
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Drawer render failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-drawer-body]');
          if (fresh) self.body.innerHTML = fresh.innerHTML;
          // The rec block is inside the body, so it was just replaced
          // with an empty one.
          self._renderRecs();
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          console.warn('[ideas] drawer render failed', err);
        })
        .then(function () {
          // Only the newest render clears the flag — an aborted one
          // must not un-busy the drawer while its replacement is still
          // in flight.
          if (self._pending === controller) {
            self._pending = null;
            self.removeAttribute('aria-busy');
          }
        });
    }

    /* ── Recommendations ─────────────────────────────────── */

    _cartProductIds() {
      var cart = window.IdeasCart && window.IdeasCart.get();
      if (!cart || !cart.items) return [];
      return cart.items.map(function (item) {
        return String(item.product_id);
      });
    }

    _renderRecs() {
      if (!this.recsOn) return;

      var block = this.querySelector('[data-rec-block]');
      var track = this.querySelector('[data-rec-track]');
      if (!block || !track) return;

      // Seed from the live cart rather than the section's data attribute:
      // only the drawer BODY is replaced on re-render, so the attribute
      // still holds whatever was in the basket at page load.
      var inCart = this._cartProductIds();
      var seed = inCart[0] || this.recSeedFallback;
      if (!seed) return;

      var self = this;
      var url =
        this.recUrl +
        '?section_id=ideas-cart-recs&limit=' +
        this.recLimit +
        '&product_id=' +
        encodeURIComponent(seed);

      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('Recommendations failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelectorAll('[data-rec-card]');
          if (!cards.length) return;

          track.innerHTML = '';
          Array.prototype.forEach.call(cards, function (card) {
            // Suggesting something already in the basket reads as broken.
            if (inCart.indexOf(String(card.dataset.productId)) !== -1) return;
            track.appendChild(card);
          });

          if (track.children.length) {
            block.hidden = false;
            self._updateRecNav();
          }
        })
        .catch(function (err) {
          console.warn('[ideas] drawer recommendations failed', err);
        });
    }

    _bindRecNav() {
      var self = this;

      this.addEventListener('click', function (event) {
        var prev = event.target.closest('[data-rec-prev]');
        var next = event.target.closest('[data-rec-next]');
        if (!prev && !next) return;

        event.preventDefault();
        var track = self.querySelector('[data-rec-track]');
        if (!track) return;

        // One card plus its gap, so a press lands on a card edge rather
        // than an arbitrary offset.
        var card = track.querySelector('[data-rec-card]');
        var step = card ? card.offsetWidth + 16 : track.clientWidth;
        track.scrollBy({ left: prev ? -step : step, behavior: 'smooth' });
      });

      this.addEventListener(
        'scroll',
        function (event) {
          if (event.target.matches && event.target.matches('[data-rec-track]')) {
            self._updateRecNav();
          }
        },
        true
      );
    }

    // Disables an arrow at the end of its travel instead of leaving a
    // control that silently does nothing.
    _updateRecNav() {
      var track = this.querySelector('[data-rec-track]');
      var prev = this.querySelector('[data-rec-prev]');
      var next = this.querySelector('[data-rec-next]');
      if (!track || !prev || !next) return;

      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
    }

    /* ── Open / close ────────────────────────────────────── */

    open() {
      if (this._open) return;
      this._open = true;
      this._lastFocus = document.activeElement;

      this.classList.add('is-open');
      document.body.classList.add('ideas-cart-open');
      document.addEventListener('keydown', this._onKeydown);

      /* Close on any click outside the panel, listening on the document
         rather than relying on the click landing on the scrim. The scrim
         is a painted element and anything that stops it covering the
         page — a stacking context from a section or an app, a CSS load
         failure — would silently make the drawer undismissable.

         Armed on the next tick so the very click that opened the drawer
         does not immediately close it again. */
      var self = this;
      this._armTimer = setTimeout(function () {
        document.addEventListener('click', self._onDocClick);
      }, 0);

      // The dialog, not its first control — focusing the close button
      // left a ring sitting on it every time the drawer opened.
      this.panel.focus({ preventScroll: true });
    }

    _onDocClick(event) {
      if (!this._open) return;
      if (event.target.closest('.ideas-drawer__panel')) return;
      this.close();
    }

    close() {
      if (!this._open) return;
      this._open = false;

      this.classList.remove('is-open');
      document.body.classList.remove('ideas-cart-open');
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('click', this._onDocClick);
      if (this._armTimer) clearTimeout(this._armTimer);

      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this._open) return;

      var items = Array.prototype.filter.call(
        this.panel.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('ideas-cart-drawer', IdeasCartDrawer);
})();
